import { Fragment, MouseEvent, useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { URLS } from '../../constants/urls';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { useTimeout } from '../../hooks/useTimeout';
import { LinkButton } from '../Button/LinkButton';
import { OrderedList } from '../List/List';
import { ListItem } from '../List/ListItem';
import { RevealText } from '../RevealText/RevealText';
import { Text } from '../Text/Text';
import { Title } from '../Title/Title';
import { VideoPlayer } from '../VideoPlayer/VideoPlayer';
import { View } from '../View/View';
import { About } from './About';
import styles from './About.module.scss';
import { AboutView } from './AboutView';

export const AboutOurPartnership = () => {
  const { isMobile } = useAppContext();
  const Component = isMobile ? Mobile : Desktop;
  return <Component />;
};

const nextLabel = 'Share our vision';
const tag = 'click_about_partnership_startfighting';

const Mobile = () => {
  const indexState = useState(0);
  return (
    <About
      items={[
        { nextLabel, tag, theme: 'dark' },
        { theme: 'dark' },
        { theme: 'light' },
        { theme: 'light' },
        { theme: 'light' },
        { theme: 'light' },
      ]}
      indexState={indexState}
    >
      <SwiperSlide>
        <AboutView sidePadding={24} background="our-partnership">
          {renderHero(indexState[0] === 0)}
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView leftPadding={0} rightPadding={0}>
          {ourPartnershipVideo}
          <View topPadding={32} leftPadding={24} rightPadding={36}>
            {ourPartnershipTitle}
            <View gap={24}>
              {ourPartnershipText}
              {ourPartnershipLogo}
            </View>
          </View>
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView mobileAlignItems="center">{ourProjects}</AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView mobileAlignItems="center" leftPadding={0} rightPadding={0}>
          <img src="/our-projects.png" alt="map" className={styles.ourProjectsMap} />
          <View sidePadding={16} topPadding={18}>
            {ourProjectsMapText}
          </View>
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView mobileAlignItems="center">{contributorsHeader}</AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView mobileAlignItems="center">
          <ContributorsAccordion {...{ contributors, isMobile: true }} />
        </AboutView>
      </SwiperSlide>
    </About>
  );
};

const Desktop = () => {
  const indexState = useState(0);
  return (
    <About
      items={[
        { nextLabel, tag, theme: 'dark' },
        { theme: 'dark' },
        { theme: 'light' },
        { theme: 'light' },
      ]}
      indexState={indexState}
    >
      <SwiperSlide>
        <AboutView sidePadding={24} background="our-partnership">
          {renderHero(indexState[0] === 0)}
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView maxWidth={1035}>
          {ourPartnershipTitle}
          {ourPartnershipVideo}
          <View
            justifyContent="space-between"
            flow="column"
            gridTemplateColumns="23.7% 73.1%"
            alignItems="center"
          >
            {ourPartnershipLogo}
            {ourPartnershipText}
          </View>
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView maxWidth={1024}>
          <View
            flow="column"
            gridTemplateColumns="37% 47.5%"
            alignItems="center"
            gap="5%"
          >
            <View gap={37}>{ourProjects}</View>
            <View gap={8}>
              <img
                src="/our-projects-lg.png"
                alt="map"
                className={styles.ourProjectsMap}
              />
              {ourProjectsMapText}
            </View>
          </View>
        </AboutView>
      </SwiperSlide>
      <SwiperSlide>
        <AboutView maxWidth={1024}>
          <View gap={32}>
            {contributorsHeader}
            {contributors.map((item) => (
              <View
                justifyContent="space-between"
                flow="column"
                gridTemplateColumns="37% 62%"
                alignItems="center"
                key={`c${item.id}`}
              >
                {renderContributorHeader(item)}
                {renderContributorQuoate(item)}
              </View>
            ))}
          </View>
        </AboutView>
      </SwiperSlide>
    </About>
  );
};

const renderHero = (when: boolean) => (
  <div>
    <RevealText
      as="h1"
      className={styles.title}
      delay={300}
      variant="button-labels"
      when={when}
    >
      Our Partnership
    </RevealText>
    <RevealText
      delay={300}
      desktopVariant="headline-xl-bold"
      variant="headline-l-bold"
      when={when}
    >
      {`We’re proud of our joint partnership with One Tree Planted`}
    </RevealText>
  </div>
);

const ourPartnershipTitle = (
  <Title as="h2" variant="headline-m" desktopVariant="headline-l-bold" divider>
    Our Partnership
  </Title>
);

const ourPartnershipText = (
  <Text variant="body-r">
    One Tree Planted is a non-profit org focused on global reforestation. Since 2014
    they have more than doubled the number of trees planted each year. We’ve partnered
    together to fund projects focused on preventing catastrophic fires.
  </Text>
);

const ourPartnershipLogo = <img src="/otp-logo.svg" alt="One Tree Planted" />;

const ourPartnershipVideo = (
  <VideoPlayer videoId="XiWNeGBkOgQ" className={styles.videoPlayer} />
);

const ourProjects = (
  <div>
    <Title as="h2" variant="headline-m" desktopVariant="headline-l-bold" divider>
      Our Projects
    </Title>
    <View gap={44}>
      <Text variant="body-s">
        Our work returns large portions of the forest to their historically
        fire-adapted condition. By impacting future fire behavior, large-scale forest
        restoration, and fuel reduction minimizes habitat risk for many of
        California’s native plant and animal species.
      </Text>
      <OrderedList text="subhead-xs">
        <ListItem>Fellowship Training for future Forestry Experts</ListItem>
        <ListItem>Fuel Reduction in the Modoc Plateau</ListItem>
        <ListItem>Fuel Reduction in Sacramento Valley</ListItem>
        <ListItem>
          Rewilding California’s Natural Ecosystems for Wildfire Resilience
        </ListItem>
        <ListItem>Restoring Native Plants for the Endangered Butterflies</ListItem>
      </OrderedList>
    </View>
  </div>
);

const ourProjectsMapText = (
  <Text variant="body-s" className={styles.ourProjectsMapText}>
    {`The map reflects California’s Resource Conservation Districts each district funds
    our four projects. Learn more about these regions and the work they do `}
    <LinkButton href={URLS.FIND_RCD} target={URLS.FIND_RCD} underline>
      here
    </LinkButton>
  </Text>
);

const contributorsHeader = (
  <div>
    <Title as="h2" variant="headline-m" desktopVariant="headline-l-bold" divider>
      Contributors and Forest Professionals
    </Title>
    <Text variant="body-r" className={styles.contributorHeaderText} as="p">
      We’ve worked in tandem with local experts across relevant fields to ensure
      success in our approach and execution. Special thanks to One Tree Planted,
      Solana, and WFRTF.
    </Text>
  </div>
);

interface Contributor {
  id: string;
  name: string;
  title: string;
  quoate: string;
}

const contributors: Contributor[] = [
  {
    id: `kyleigh-hughes`,
    name: `Kyleigh Hughes`,
    title: `Strategic Partnerships & Projects Manager, One Tree Planted`,
    quoate: `“FIREWATCH’s educational component will teach Californians about the aspects of restoration that are important to protect California's forests.“`,
  },
  {
    id: `stewart-mcmorrow`,
    name: `Stewart Mcmorrow`,
    title: `Staff Chief, Wildfire and Forest Resilience Task Force`,
    quoate: `“FIREWATCH is going to encourage a long term relationship with the landscape and the people who are investing in their particular area — And I think that's really what we need to see.”`,
  },
  {
    id: `anatoly-yakovenko`,
    name: `Anatoly Yakovenko`,
    title: `CEO, Solana`,
    quoate: `“Solana’s unique advantages of speed, low cost, and energy efficiency provide an excellent foundation for FIREWATCH to make an impact.”`,
  },
];

const renderContributorHeader = (
  { id, name, title }: Contributor,
  isMobile?: boolean
) => (
  <View
    flow="column"
    gap={8}
    gridTemplateColumns="min-content"
    className={styles.contributorHeader}
  >
    <img
      src={`/contributor-${id}${isMobile ? '' : '-lg'}.png`}
      alt={`${name} portrait`}
      className={styles.contributorPortrait}
    />
    <View gap={8}>
      <Title as="h3" variant="subhead-s">
        {name}
      </Title>
      <Text variant="body-r" desktopVariant="body-s">
        {title}
      </Text>
    </View>
  </View>
);

const renderContributorQuoate = ({ quoate }: Contributor) => (
  <Text variant="headline-xs" className={styles.contributorQuote} as="p">
    {quoate}
  </Text>
);

interface ContributorsAccordionDetails {
  details: HTMLDetailsElement;
  summary: HTMLElement;
  content: HTMLDivElement;
  quote: HTMLParagraphElement;
}

const ContributorsAccordion = ({
  contributors,
  isMobile,
}: {
  contributors: Contributor[];
  isMobile?: boolean;
}) => {
  const ref = useRef<HTMLElement>(null);
  const detailsRef = useRef<ContributorsAccordionDetails[]>([]);
  const { tidRef, killTimeout } = useTimeout();

  const setHeight = (activeItem: ContributorsAccordionDetails) => {
    killTimeout();
    tidRef.current = setTimeout(() => {
      detailsRef.current.forEach((item) => {
        if (activeItem !== item) {
          item.details.open = false;
        } else {
          const { height } = activeItem.quote.getBoundingClientRect();
          activeItem.content.style.setProperty('height', `${height}px`);
        }
      });
    }, 150);
  };

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const activeItem = detailsRef.current.find(
      ({ summary }) => summary === event.currentTarget
    );
    if (!activeItem || activeItem.details.open) {
      event.preventDefault();
      return;
    }
    detailsRef.current.forEach((item) => {
      if (activeItem !== item) item.content.style.removeProperty('height');
    });
    setHeight(activeItem);
  };

  useEffect(() => {
    if (!ref.current) return;
    const items = Array.from(ref.current.querySelectorAll('details')).map(
      (details) =>
        ({
          details,
          summary: details.querySelector(`.${styles.accordionSummary}`),
          content: details.querySelector(`.${styles.accordionContent}`),
          quote: details.querySelector(`.${styles.contributorQuote}`),
        } as ContributorsAccordionDetails)
    );
    detailsRef.current = items;
    const activeItem = items[0];
    if (activeItem) {
      activeItem.details.open = true;
      setHeight(activeItem);
    }
  }, []);

  return (
    <View gap={24} ref={ref}>
      {contributors.map((item) => (
        <Fragment key={`a${item.id}`}>
          <details>
            <summary className={styles.accordionSummary} onClick={onClick}>
              {renderContributorHeader(item, isMobile)}
            </summary>
            <div className={styles.accordionContent}>
              {renderContributorQuoate(item)}
            </div>
          </details>
        </Fragment>
      ))}
    </View>
  );
};
